import CommentIcon from '@mui/icons-material/Comment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MessageIcon from '@mui/icons-material/Message';
import TableViewIcon from '@mui/icons-material/TableView';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Box, Card, CircularProgress, IconButton, MenuItem, Select, styled, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { crmModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';
import MessagePostGrid from './Grid/messageGrid.component';

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '60px',
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiInputBase-input': {
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

// Custom styled download button
const DownloadButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: '6px', // Reduced from 10px
  borderRadius: '6px', // Adjusted for smaller size
  transition: 'all 0.3s ease',
  width: '36px', // Fixed width for compactness
  height: '36px', // Fixed height for compactness
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[4],
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
  '&.loading': {
    animation: 'pulse 1.5s infinite',
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.03)' }, // Adjusted for smaller button
    '100%': { transform: 'scale(1)' },
  },
  // Ensure icons are slightly smaller
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem', // Smaller icon size (default is ~1.5rem for medium)
  },
}));

const CRMBodyComponent = ({ connectedProfileId, serviceName, startDate, endDate }) => {
  const [isLoading, setLoading] = useState(true);
  const [crm, setCRM] = useState([]);
  const { showToast } = useToaster();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_time');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedType, setSelectedType] = useState('message');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  useEffect(() => {
    const options = getTypeOptions();
    if (options.length > 0) {
      setSelectedType(options[0].value);
      getCRMDetails(order, orderBy, searchText, rowsPerPage, options[0].value);
    }
  }, [connectedProfileId, startDate, endDate, serviceName]);

  const successCallback = (data) => {
    setCRM(data);
    setTotalCount(data?.length || 0);
    setLoading(false);
  };

  const nextCrmSuccessCallback = (data) => {
    setCRM((prev) => [...prev, ...data]);
    setTotalCount(totalCount + data?.length || 0);
    setLoading(false);
  };

  const failureCallback = (message) => {
    setCRM([]);
    message && showToast(message, 'e');
    setLoading(false);
  };

  const getCRMDetails = (
    orderVal = order,
    orderByVal = orderBy,
    searchVal = searchText,
    rowsPerPageVal = rowsPerPage,
    typeFilter = selectedType
  ) => {
    setLoading(true);
    const typeParam = typeFilter ? `&type=${typeFilter}` : '';
    const getProps = {
      url: `${
        crmModuleURL.fetchWebhookMessage
      }${serviceName}&connected_profile_id=${connectedProfileId}${typeParam}&order=${orderVal}&orderby=${orderByVal}&search=${encodeURIComponent(
        searchVal
      )}&page=${0}&page_size=${rowsPerPageVal}&start_date=${startDate}&end_date=${endDate}`,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getNexCRMDetails = (
    orderVal = order,
    orderByVal = orderBy,
    searchVal = searchText,
    pageVal = page,
    rowsPerPageVal = rowsPerPage,
    typeFilter = selectedType
  ) => {
    const typeParam = typeFilter ? `&type=${typeFilter}` : '';
    const getProps = {
      url: `${
        crmModuleURL.fetchWebhookMessage
      }${serviceName}&connected_profile_id=${connectedProfileId}${typeParam}&order=${orderVal}&orderby=${orderByVal}&search=${encodeURIComponent(
        searchVal
      )}&page=${pageVal * rowsPerPageVal + 1}&page_size=${rowsPerPageVal}&start_date=${startDate}&end_date=${endDate}`,
      successCallback: nextCrmSuccessCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const handleStatusChange = (id, newStatus) => {
    if (newStatus === '') {
      return;
    }
    setLoading(true);
    const updateProps = {
      url: crmModuleURL.updateWebhookMessage,
      body: {
        id: id,
        status: parseInt(newStatus),
      },
      successCallback: (data, message) => {
        message && showToast(message, 's');
        setTimeout(() => {
          getCRMDetails(order, orderBy, searchText, rowsPerPage);
        }, 1000);
      },
      failureCallback: (message) => {
        message && showToast(message, 'e');
        setLoading(false);
      },
    };
    HttpServices.Post(updateProps);
  };

  const handleReCallCRM = (orderVal, orderByVal, search) => {
    setPage(0);
    setOrder(orderVal);
    setOrderBy(orderByVal);
    setSearchText(search);
    getCRMDetails(orderVal, orderByVal, search);
  };

  const handleTypeFilter = (type) => {
    setSelectedType(type);
    setPage(0);
    getCRMDetails(order, orderBy, searchText, rowsPerPage, type);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if ((newPage + 1) * rowsPerPage > crm.length) {
      getNexCRMDetails(order, orderBy, searchText, newPage, rowsPerPage, selectedType);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPage(0);
    setRowsPerPage(newRowsPerPage);
    if (newRowsPerPage > crm?.length) {
      getCRMDetails(order, orderBy, searchText, newRowsPerPage);
    }
  };

  const getTypeOptions = () => {
    if (serviceName === 'instagram') {
      return [
        {
          value: 'message',
          label: 'Message',
          icon: <MessageIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'comment',
          label: 'Comment',
          icon: <CommentIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
      ];
    } else if (serviceName === 'facebook') {
      return [
        {
          value: 'message',
          label: 'Message',
          icon: <MessageIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'comment',
          label: 'Comment',
          icon: <CommentIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'like',
          label: 'Like',
          icon: <ThumbUpIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
      ];
    }
    return [];
  };

  const handleDownloadCSV = () => {
    setIsDownloading(true);
    const typeParam = selectedType ? `&type=${selectedType}` : '';
    var page = 1;
    var size = crm?.length > 20 ? crm?.length : rowsPerPage;
    const getProps = {
      url: `${
        crmModuleURL.exportWebhookMessage
      }${serviceName}&connected_profile_id=${connectedProfileId}${typeParam}&order=${order}&orderby=${orderBy}&search=${encodeURIComponent(
        searchText
      )}&page=${page}&page_size=${size}&start_date=${startDate}&end_date=${endDate}`,
      successCallback: (data, message) => {
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const filename = `${serviceName}_${selectedType}_messages_${new Date().toISOString().slice(0, 10)}.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        setIsDownloading(false);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        message && showToast(message, 'e');
        setIsDownloading(false);
      },
    };
    HttpServices.DownloadCSV(getProps);
  };

  const handleDownloadExcel = () => {
    setIsDownloadingExcel(true);
    const typeParam = selectedType ? `&type=${selectedType}` : '';
    var page = 1;
    var size = crm?.length > 20 ? crm?.length : rowsPerPage;
    const getProps = {
      url: `${
        crmModuleURL.exportWebhookMessageExcel
      }${serviceName}&connected_profile_id=${connectedProfileId}${typeParam}&order=${order}&orderby=${orderBy}&search=${encodeURIComponent(
        searchText
      )}&page=${page}&page_size=${size}&start_date=${startDate}&end_date=${endDate}`,
      successCallback: (data, message) => {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const filename = `${serviceName}_${selectedType}_messages_${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        setIsDownloadingExcel(false);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        message && showToast(message, 'e');
        setIsDownloadingExcel(false);
      },
    };
    HttpServices.DownloadExcel(getProps);
  };

  return (
    <Box>
      {isLoading ? (
        <Card style={{ minHeight: 'calc(100vh - 270px)', maxHeight: 'calc(100vh - 270px)' }}>
          <Spinner />
        </Card>
      ) : (
        <>
          {crm && crm.length > 0 ? (
            <Card>
              <Box display="flex" justifyContent="flex-end" p={1.3} gap={1}>
                <Tooltip title="Download CSV">
                  <DownloadButton
                    onClick={handleDownloadCSV}
                    disabled={isDownloading}
                    className={isDownloading ? 'loading' : ''}
                  >
                    {isDownloading ? <CircularProgress size={24} color="inherit" /> : <FileDownloadIcon />}
                  </DownloadButton>
                </Tooltip>
                <Tooltip title="Download Excel">
                  <DownloadButton
                    onClick={handleDownloadExcel}
                    disabled={isDownloadingExcel}
                    className={isDownloadingExcel ? 'loading' : ''}
                  >
                    {isDownloadingExcel ? <CircularProgress size={24} color="inherit" /> : <TableViewIcon />}
                  </DownloadButton>
                </Tooltip>
              </Box>
              <MessagePostGrid
                connectedProfileId={connectedProfileId}
                crmList={crm}
                handleReCallCRM={handleReCallCRM}
                handleTypeFilter={handleTypeFilter}
                serviceName={serviceName}
                orderBy={orderBy}
                order={order}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                handleStatusChange={handleStatusChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                selectedType={selectedType}
              />
            </Card>
          ) : (
            <Card
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 750,
                minHeight: 'calc(100vh - 200px)',
                maxHeight: 'calc(100vh - 200px)',
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                No {selectedType} data available. Please select a different type.
              </Typography>
              <StyledSelect
                value={selectedType}
                onChange={(e) => handleTypeFilter(e.target.value)}
                displayEmpty
                sx={{ minWidth: 200 }}
              >
                {getTypeOptions().map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {option.icon}
                    <Typography variant="body2">{option.label}</Typography>
                  </MenuItem>
                ))}
              </StyledSelect>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default CRMBodyComponent;
